import { useCallback, useEffect, useRef, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { Outlet } from "react-router-dom";
import CadastrosMuiNav from "./CadastrosMuiNav";
import CadastrosMuiHeader from "./CadastrosMuiHeader";
import { useColorMode } from "../../../contexts/ColorModeContext";
import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from "./navConfig";

const HOVER_OPEN_DELAY_MS = 40;
const HOVER_CLOSE_DELAY_MS = 220;

const CadastrosMuiLayout = () => {
  const { mode } = useColorMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [pinnedCollapsed, setPinnedCollapsed] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const hoverTimerRef = useRef(null);

  const expanded = !pinnedCollapsed || hovered;
  const drawerWidth = isMobile
    ? 0
    : expanded
      ? DRAWER_WIDTH
      : DRAWER_WIDTH_COLLAPSED;

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const handleHoverChange = useCallback(
    (isHovering) => {
      if (!pinnedCollapsed) {
        clearHoverTimer();
        setHovered(false);
        return;
      }

      clearHoverTimer();
      hoverTimerRef.current = setTimeout(
        () => setHovered(isHovering),
        isHovering ? HOVER_OPEN_DELAY_MS : HOVER_CLOSE_DELAY_MS
      );
    },
    [pinnedCollapsed, clearHoverTimer]
  );

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  useEffect(() => {
    if (!pinnedCollapsed) {
      clearHoverTimer();
      setHovered(false);
    }
  }, [pinnedCollapsed, clearHoverTimer]);

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  return (
    <Box
      data-cad-mui-mode={mode}
      sx={{
        display: "flex",
        width: "100%",
        height: "100dvh",
        bgcolor: "background.default",
        color: "text.primary",
        transition: "background-color 0.25s ease, color 0.25s ease",
        overflow: "hidden",
      }}
    >
      <CadastrosMuiNav
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        expanded={expanded}
        pinnedCollapsed={pinnedCollapsed}
        onToggleCollapse={() => setPinnedCollapsed((value) => !value)}
        onHoverChange={handleHoverChange}
      />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
          transition: "width 280ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <CadastrosMuiHeader
          onMenuClick={isMobile ? () => setMobileOpen(true) : undefined}
        />
        <Box
          className="cad-mui-content"
          sx={(themeArg) => ({
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.default",
            color: "var(--ctrl-text)",
            backgroundImage:
              themeArg.palette.mode === "dark"
                ? "radial-gradient(ellipse at top right, rgba(91,163,188,0.08), transparent 55%)"
                : "radial-gradient(ellipse at top right, rgba(232,163,23,0.08), transparent 55%)",
          })}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default CadastrosMuiLayout;
